import axios, { AxiosError, AxiosResponse } from 'axios';
axios.defaults.withCredentials = true;
import store from '@/store';
import { logout } from '../requests/auth';

import router from '@/router';
export let urlBaseEndpoint = 'https://werberat.k25-dev.at/werberat-backend/api';

if (location.host.includes('werberat.klickdrauf.at')) {
	urlBaseEndpoint = 'https://werberat.klickdrauf.at/werberat-backend/api';
} else if (location.host.indexOf('beschwerde.werberat.at') !== -1) {
	urlBaseEndpoint = 'https://beschwerde.werberat.at/werberat-backend/api';
}

export type SuccessHttpResponse<T> = {
	success: true;
	data: T;
	status: number;
	error: null;
	headers?: any;
};

export type ErrorHttpResponse = {
	success: false;
	data: null;
	status: number;
	error: any;
};

type HttpMethod = 'get' | 'post' | 'put' | 'delete';

declare module 'axios' {
	interface AxiosRequestConfig {
		/** Bei false führt ein 404 nicht zur Fehlerseite (für Nebenbereiche wie den Verlauf). Standard: true. */
		redirectOn404?: boolean;
	}
}

let isLoggingOut = false;

async function handleLogout() {
	if (isLoggingOut) return;

	isLoggingOut = true;
	try {
		const logoutResult = await logout();

		store.dispatch('logout');

		const currentRoute = router.currentRoute.value;
		if (currentRoute.name !== 'Login') {
			if (currentRoute.name !== 'Fehler') {
				store.dispatch('setIntendedUrl', currentRoute.fullPath);
			}
			router.push({ name: 'Login' });
		}

		if (!logoutResult.success) {
			console.warn('Backend logout failed, but user was logged out in frontend');
		}
	} catch (error) {
		console.error('Error during logout:', error);
	} finally {
		isLoggingOut = false;
	}
}

export type HttpResponse<T> = SuccessHttpResponse<T> | ErrorHttpResponse;

axios.interceptors.response.use(
	(response: AxiosResponse) => {
		return response;
	},
	async (error: AxiosError) => {
		const isLoggedIn = store.state.loggedin;

		if (error.code === 'ERR_NETWORK' && isLoggedIn) {
			await handleLogout();
		}

		if (error.response?.status === 401 && isLoggedIn) {
			await handleLogout();
		} else if (error.response?.status === 404 && error.config?.redirectOn404 !== false) {
			router.push({ name: 'Fehler', params: { errorType: '404' } });
		}

		return Promise.reject(error);
	},
);

export async function makeRequest<TRequestDto, TResponseDto>(
	url: string,
	requestData: TRequestDto | null,
	method: HttpMethod,
	formdata: boolean = false,
	redirectOn404: boolean = true,
): Promise<HttpResponse<TResponseDto>> {
	try {
		const response: AxiosResponse = await axios({
			method: method,
			url: `${urlBaseEndpoint}/${url}`,
			data: requestData,
			withCredentials: true,
			redirectOn404,
			headers: {
				Accept: 'application/json',
				'Content-Type': formdata ? 'multipart/form-data' : 'application/json',
			},
		});
		// 204 No Content (Command-Endpoints seit OEWR-91): axios liefert '' — als null weitergeben
		const hasBody = response.status !== 204 && response.data !== '' && response.data !== undefined;
		const result: SuccessHttpResponse<TResponseDto> = {
			success: true,
			data: hasBody ? response.data : (null as TResponseDto),
			status: response.status,
			error: null,
			headers: response.headers,
		};
		return result;
	} catch (error) {
		let errorResult: ErrorHttpResponse;

		if (axios.isAxiosError(error)) {
			errorResult = {
				success: false,
				data: null,
				status: error.response?.status || 500,
				error: error.response?.data || error.message,
			};
		} else {
			errorResult = {
				success: false,
				data: null,
				status: 500,
				error: 'An unexpected error occurred',
			};
		}

		return errorResult;
	}
}
